import { Component, computed, inject, input, output } from '@angular/core';
import {
  ZiroExplorer,
  ZiroExplorerColumn,
  ZiroExplorerNode,
  ZiroExplorerSubchart,
} from '../../../../shared/explorer/explorer-models';
import {
  ButtonAction,
  ButtonDestination,
  ButtonDestinationType,
  CallHandler,
  CallHandlerButton,
  CallHandlerButtons,
  CiscoPhoneNumberReport,
  CucAutoAttendantsReport,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { TranslateService } from '@ngx-translate/core';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';
import { MigrationsCiscoAutoAttendantsScheduleService } from './migrations-cisco-auto-attendants-schedule.service';

type TransferOptionType = 'Standard' | 'Off Hours' | 'Alternate';

interface TransferOptionActions {
  standardGreetingTransfersToGreeting: boolean;
  offHoursTransfersToGreeting: boolean;
  alternateTransfersToGreeting: boolean;
}

interface TransferOptionConfigurations {
  isStandardGreetingConfigured: boolean;
  isOffHoursGreetingConfigured: boolean;
  isAlternateGreetingConfigured: boolean;
}

@Component({
  selector: 'ziro-migrations-cisco-auto-attendants-explorer',
  templateUrl: 'migrations-cisco-auto-attendants-explorer.component.html',
  standalone: false,
  providers: [MigrationsCiscoAutoAttendantsScheduleService],
})
export class MigrationsCiscoAutoAttendantsExplorerComponent {
  private _translateService = inject(TranslateService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _migrationsCiscoAutoAttendantsScheduleService = inject(MigrationsCiscoAutoAttendantsScheduleService);

  readonly autoAttendantsReport = input.required<CucAutoAttendantsReport>();

  readonly directoryNumbersReport = input<CiscoPhoneNumberReport>();

  private readonly _transferRulesInfoMap = new Map<TransferOptionType, { title: string; icon: ZiroIcons }>([
    [
      'Standard',
      {
        title: 'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.standard',
        icon: ZiroIcons.BUSINESS_HOURS,
      },
    ],
    [
      'Off Hours',
      {
        title: 'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours',
        icon: ZiroIcons.HOLIDAY_HOURS,
      },
    ],
    [
      'Alternate',
      {
        title: 'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.alternate',
        icon: ZiroIcons.GREETING_ALTERNATE,
      },
    ],
  ]);

  private readonly _orderedTouchtoneKeys: (keyof CallHandlerButtons)[] = [
    'button1',
    'button2',
    'button3',
    'button4',
    'button5',
    'button6',
    'button7',
    'button8',
    'button9',
    'button0',
    'buttonStar',
    'buttonPound',
  ];
  private readonly _goBackTouchtoneKeys: (keyof CallHandlerButtons)[] = ['button0', 'buttonStar', 'buttonPound'];

  readonly activeGreetingAudioUrl = input<string>();
  readonly activeGreetingAudioLoadingUrl = input<string>();
  readonly activeGreetingAudioDownloadUrl = input<string>();
  readonly playGreetingAudio = output<string>();
  readonly stopGreetingAudio = output<void>();
  readonly downloadGreetingAudio = output<string>();

  config = computed<ZiroExplorer>(() => {
    const report = this.autoAttendantsReport();
    const subcharts: ZiroExplorerSubchart[] = report.callHandlers.map((callHandler) => {
      const entryPoints = this._determineEntryPoints(callHandler, report);
      const entryPointsColumn: ZiroExplorerColumn = {
        header: 'tkey;migrations.cisco.auto_attendants_report.explorer.entry_points.header',
        nodes: entryPoints,
        alertWhenNoNodesVisible: 'tkey;migrations.cisco.auto_attendants_report.explorer.entry_points.no_sources_alert',
      };

      const greetings = this._determineGreetings(callHandler, entryPoints);
      const greetingsColumn: ZiroExplorerColumn = {
        header: 'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.header',
        nodes: greetings,
      };

      const destinations = this._determineDestinations(callHandler, report, greetings);
      const destinationsColumn: ZiroExplorerColumn = {
        header: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.header',
        nodes: destinations,
      };

      return {
        id: callHandler.id,
        columns: [entryPointsColumn, greetingsColumn, destinationsColumn],
      };
    });
    return { subcharts };
  });

  private _determineEntryPoints(callHandler: CallHandler, report: CucAutoAttendantsReport): ZiroExplorerNode[] {
    const nodes: ZiroExplorerNode[] = [];

    if (callHandler.extension) {
      const extension = this._telephoneNumberFilter.transform(callHandler.extension);
      nodes.push({
        title: 'tkey;migrations.cisco.auto_attendants_report.explorer.entry_points.directory_number',
        group: 'entry points',
        label: this._translateService.instant(
          'tkey;migrations.cisco.auto_attendants_report.explorer.entry_points.directory_number.label',
          { extension: extension },
        ),
        sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.entry_points.directory_number.sublabel',
        icon: ZiroIcons.HASH,
        afterConnection: 'downward',
      });
    }

    const linksFromOtherCallHandlers: ZiroExplorerNode[] = report.callHandlers
      .filter((otherHandler) => {
        const callHandlerButtons = Object.entries(otherHandler.processedCallerInputs) as [
          keyof CallHandlerButtons,
          CallHandlerButton,
        ][];
        const linkFromOtherHandler = callHandlerButtons.find(
          ([, buttonValue]) => buttonValue.buttonDestination?.id === callHandler.id,
        );
        const isLinkOnBackButton = linkFromOtherHandler && this._goBackTouchtoneKeys.includes(linkFromOtherHandler[0]);
        const processedCallerInputs = Object.values(callHandler.processedCallerInputs) as CallHandlerButton[];
        const isOtherHandlerReferencedByThisOne = processedCallerInputs.some(
          (callerInput) => callerInput.buttonDestination?.id === otherHandler.id,
        );
        return linkFromOtherHandler && (!isLinkOnBackButton || !isOtherHandlerReferencedByThisOne);
      })
      .map((otherHandler) => {
        const extension = this._telephoneNumberFilter.transform(otherHandler.extension);
        let label = otherHandler.displayName;
        if (extension) {
          label += ` (${extension})`;
        }
        return {
          title: 'tkey;migrations.cisco.auto_attendants_report.explorer.entry_points.call_handler',
          group: 'entry points',
          label: label,
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.entry_points.call_handler.sublabel',
          icon: ZiroIcons.AUTO_ATTENDANT,
          afterConnection: 'downward',
          previousSubchartLink: otherHandler.id,
          previouslyViewedTooltip: 'tkey;migrations.cisco.auto_attendants_report.explorer.previously_viewed.tooltip',
        };
      });
    nodes.push(...linksFromOtherCallHandlers);

    if (nodes.length >= 1) {
      nodes[nodes.length - 1].afterConnection = 'across';
    }

    return nodes;
  }

  private _buildGreetingNodes(
    callHandler: CallHandler,
    transferOption: { [key: string]: string },
    optionActions: TransferOptionActions,
    optionConfigurations: TransferOptionConfigurations,
  ): ZiroExplorerNode {
    const transferOptionType = transferOption['transferOptionType'] as TransferOptionType;

    const audioPrompt = this._getAudioForType(transferOptionType, callHandler.greetingAudioInformation);
    const audioType = audioPrompt?.audioType;
    const audioFileUrls = audioPrompt?.audioFileUrls;
    const mediaDisabled = audioType !== 'CUSTOM' || !audioFileUrls.length;

    const transferRulesInfo = this._transferRulesInfoMap.get(transferOptionType);
    const node = {
      title: transferRulesInfo.title,
      icon: transferRulesInfo.icon,
      audioFileUrls,
      mediaDisabled,
    };

    const offHoursTransferOption = callHandler.transferOptions.find(
      (option) => option['transferOptionType'] === 'Off Hours',
    );
    const offHoursGreeting = callHandler.greetingInformation.find(
      (greeting) => greeting['greetingType'] === 'Off Hours',
    );
    const holidayGreeting = callHandler.greetingInformation.find((greeting) => greeting['greetingType'] === 'Holiday');
    const scheduleLength = callHandler.schedules.length;
    const warnings: string[] = [];

    switch (transferOptionType) {
      case 'Standard':
        if (scheduleLength > 1) {
          const offHoursTransferDisabled = offHoursTransferOption?.['enabled'] === 'false';
          const offHoursGreetingDisabled = offHoursGreeting?.['enabled'] === 'false';

          if (offHoursTransferDisabled && offHoursGreetingDisabled) {
            warnings.push(
              'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours_transfer_and_greeting_warning',
            );
          } else {
            if (offHoursTransferDisabled) {
              warnings.push(
                'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours_transfer_warning',
              );
            }
            if (offHoursGreetingDisabled) {
              warnings.push(
                'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.off_hours_greeting_warning',
              );
            }
          }
        }

        return {
          ...node,
          group: optionActions.standardGreetingTransfersToGreeting ? 'greetings' : 'Standard extension',
          sublabel: this._migrationsCiscoAutoAttendantsScheduleService.determineBusinessHours(callHandler.schedules),
          warnings: warnings,
          beforeConnection:
            optionConfigurations.isOffHoursGreetingConfigured || optionConfigurations.isAlternateGreetingConfigured
              ? 'downward'
              : 'across',
          afterConnection:
            optionActions.standardGreetingTransfersToGreeting &&
            (optionActions.offHoursTransfersToGreeting || optionActions.alternateTransfersToGreeting)
              ? 'downward'
              : 'across',
        };
      case 'Off Hours':
        if (callHandler.holidays.length > 0 && holidayGreeting?.['enabled'] === 'false') {
          warnings.push('tkey;migrations.cisco.auto_attendants_report.explorer.greetings.holidays_greeting_warning');
        }

        return {
          ...node,
          group: optionActions.offHoursTransfersToGreeting ? 'greetings' : 'Off Hours extension',
          sublabel: this._migrationsCiscoAutoAttendantsScheduleService.determineHolidayHours(callHandler.holidays),
          warnings: warnings,
          beforeConnection:
            optionConfigurations.isAlternateGreetingConfigured &&
            (optionActions.offHoursTransfersToGreeting || !optionActions.alternateTransfersToGreeting)
              ? 'downward'
              : 'across',
          afterConnection:
            optionActions.offHoursTransfersToGreeting && optionActions.alternateTransfersToGreeting
              ? 'downward'
              : 'across',
        };
      case 'Alternate':
        return {
          ...node,
          label: optionActions.alternateTransfersToGreeting
            ? 'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.transfer_to_greeting'
            : 'tkey;migrations.cisco.auto_attendants_report.explorer.greetings.transfer_to_extension',
          group: optionActions.alternateTransfersToGreeting ? 'greetings' : 'Alternate extension',
          beforeConnection:
            optionActions.alternateTransfersToGreeting &&
            optionConfigurations.isOffHoursGreetingConfigured &&
            !optionActions.offHoursTransfersToGreeting
              ? 'downward'
              : 'across',
          afterConnection: 'across',
        };
      default:
        break;
    }
  }

  private _getAudioForType(type: string, greetingAudioInformation: any): any {
    switch (type) {
      case 'Standard':
        return greetingAudioInformation.businessHours;
      case 'Off Hours':
        return greetingAudioInformation.offHours;
      case 'Alternate':
        return greetingAudioInformation.holidayHours;
      default:
        return undefined;
    }
  }

  private _determineGreetings(callHandler: CallHandler, entryPoints: ZiroExplorerNode[]): ZiroExplorerNode[] {
    // action: "0" === transfer to greeting (attach to buttons)
    // action: "1" === transfer to extension (attach to single node)
    // Standard/Business hours greeting always at top, others below buttons when transfer to extension

    const standardGreeting = callHandler.transferOptions.find((option) => option['transferOptionType'] === 'Standard');
    const standardGreetingTransfersToGreeting = standardGreeting['action'] === '0';

    const offHoursGreeting = callHandler.transferOptions.find((option) => option['transferOptionType'] === 'Off Hours');

    const offHoursTransfersToGreeting = !!offHoursGreeting && offHoursGreeting['action'] === '0';

    const alternateGreeting = callHandler.transferOptions.find(
      (option) => option['transferOptionType'] === 'Alternate' && option['enabled'] === 'true',
    );
    const alternateTransfersToGreeting = !!alternateGreeting && alternateGreeting['action'] === '0';

    let orderedTransferOptions: string[];
    if (alternateTransfersToGreeting && !offHoursTransfersToGreeting) {
      orderedTransferOptions = ['Standard', 'Alternate', 'Off Hours'];
    } else {
      orderedTransferOptions = ['Standard', 'Off Hours', 'Alternate'];
    }

    const sortedTransferOptions = callHandler.transferOptions
      .filter((option) => {
        if (option['transferOptionType'] === 'Off Hours') {
          return orderedTransferOptions.includes(option['transferOptionType']);
        }

        return option['enabled'] === 'true' && orderedTransferOptions.includes(option['transferOptionType']);
      })
      .sort(
        (a, b) =>
          orderedTransferOptions.indexOf(a['transferOptionType']) -
          orderedTransferOptions.indexOf(b['transferOptionType']),
      )
      .map((transferOption) => {
        return this._buildGreetingNodes(
          callHandler,
          transferOption,
          {
            standardGreetingTransfersToGreeting: standardGreetingTransfersToGreeting,
            offHoursTransfersToGreeting: offHoursTransfersToGreeting,
            alternateTransfersToGreeting: alternateTransfersToGreeting,
          },
          {
            isStandardGreetingConfigured: !!standardGreeting,
            isOffHoursGreetingConfigured: !!offHoursGreeting,
            isAlternateGreetingConfigured: !!alternateGreeting,
          },
        );
      });

    if (!entryPoints.length) {
      sortedTransferOptions.forEach((node) => delete node.beforeConnection);
    }

    return sortedTransferOptions;
  }

  private _determineDestinations(
    callHandler: CallHandler,
    report: CucAutoAttendantsReport,
    greetingNodes: ZiroExplorerNode[],
  ): ZiroExplorerNode[] {
    const nodes: ZiroExplorerNode[] = [];
    greetingNodes
      .filter((node) => node.afterConnection === 'across')
      .forEach((greetingNode: ZiroExplorerNode) => {
        if (greetingNode.group === 'greetings') {
          // Insert touchtone buttons
          const destinationEntries = Object.entries(callHandler.processedCallerInputs) as [
            keyof CallHandlerButtons,
            CallHandlerButton,
          ][];
          const sortedTouchtoneDestinations = destinationEntries.sort(([a], [b]) => {
            return this._orderedTouchtoneKeys.indexOf(a) - this._orderedTouchtoneKeys.indexOf(b);
          });
          const touchtoneNodes = sortedTouchtoneDestinations.map((callerInputEntry) =>
            this._getDestinationAction(callHandler, callerInputEntry[1], callerInputEntry[0], report),
          );
          touchtoneNodes[touchtoneNodes.length - 1].beforeConnection = 'across';
          nodes.push(...touchtoneNodes);
        } else {
          // Insert 'Transfer to Extension' node
          const transferOption = callHandler.transferOptions.find(
            (option) => option['transferOptionType'] === greetingNode.group.replace(' extension', ''),
          );
          nodes.push({
            title: this._translateService.instant(
              'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.transfer_rule',
            ),
            group: `${transferOption['transferOptionType']} extension`,
            label: this._telephoneNumberFilter.transform(transferOption['extension']),
            sublabel:
              'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.transfer_to_extension.sublabel',
            icon: ZiroIcons.USER,
            beforeConnection: 'across',
          });
        }
      });

    return nodes;
  }

  private _notUsedButtonTitle(key: string): string {
    const base = this._translateService.instant(
      'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.button',
      { buttonNumber: key },
    );
    const notUsed = this._translateService.instant(
      'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.not_used.label',
    );
    return `${base} - ${notUsed}`;
  }

  private _getDestinationAction(
    callHandler: CallHandler,
    callHandlerButton: CallHandlerButton,
    buttonNumber: keyof CallHandlerButtons,
    report: CucAutoAttendantsReport,
  ): ZiroExplorerNode {
    const buttonKey = this._getDestinationButtonNumber(buttonNumber);
    const title = this._translateService.instant(
      'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.button',
      { buttonNumber: buttonKey },
    );
    switch (callHandlerButton.buttonAction) {
      case ButtonAction.IGNORED:
        return {
          title: this._notUsedButtonTitle(buttonKey),
          group: 'greetings',
          beforeConnection: 'downward',
          disabled: true,
        };
      case ButtonAction.HANG_UP:
        return {
          title,
          group: 'greetings',
          label: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.hang_up.label',
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.hang_up.sublabel',
          beforeConnection: 'downward',
        };
      case ButtonAction.TRANSFER_TO_UNITY_OBJECT:
        return this._getDestinationForTransferAction(
          callHandler,
          callHandlerButton.buttonDestination,
          buttonNumber,
          title,
          report,
        );
      case ButtonAction.ERROR_GREETING:
        return {
          title,
          group: 'greetings',
          label: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.error.label',
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.error.sublabel',
          beforeConnection: 'downward',
        };
      case ButtonAction.TAKE_MESSAGE:
        return {
          title,
          group: 'greetings',
          label: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.message.label',
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.message.sublabel',
          beforeConnection: 'downward',
        };
      case ButtonAction.SKIP_GREETING:
        return {
          title,
          group: 'greetings',
          label: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.skip.label',
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.skip.sublabel',
          beforeConnection: 'downward',
        };
      case ButtonAction.RESTART_GREETING:
        return {
          title,
          group: 'greetings',
          label: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.restart.label',
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.restart.sublabel',
          beforeConnection: 'downward',
        };
      case ButtonAction.TRANSFER_TO_DN:
        return {
          title,
          group: 'greetings',
          label: this._getTransferLabel(callHandlerButton),
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.transfer_to_extension.sublabel',
          icon: ZiroIcons.USER,
          beforeConnection: 'downward',
        };
      case ButtonAction.ROUTE_FROM_NEXT_CALLING_ROUTING_RULE:
        return {
          title,
          group: 'greetings',
          label: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.route_to_next.label',
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.route_to_next.sublabel',
          beforeConnection: 'downward',
        };
    }
  }

  private _getDestinationButtonNumber(buttonNumber: keyof CallHandlerButtons): string {
    switch (buttonNumber) {
      case 'button1':
        return '1';
      case 'button2':
        return '2';
      case 'button3':
        return '3';
      case 'button4':
        return '4';
      case 'button5':
        return '5';
      case 'button6':
        return '6';
      case 'button7':
        return '7';
      case 'button8':
        return '8';
      case 'button9':
        return '9';
      case 'button0':
        return '0';
      case 'buttonStar':
        return '*';
      case 'buttonPound':
        return '#';
    }
  }

  private _getTransferLabel(callHandlerButton: CallHandlerButton): string {
    let label = this._telephoneNumberFilter.transform(callHandlerButton.buttonDestination.extension);
    if (callHandlerButton.buttonDestination.displayName) {
      label = `${callHandlerButton.buttonDestination.displayName} (${label})`;
    }
    return label;
  }

  private _getDestinationForTransferAction(
    callHandler: CallHandler,
    destination: ButtonDestination,
    buttonNumber: keyof CallHandlerButtons,
    nodeTitle: string,
    report: CucAutoAttendantsReport,
  ): ZiroExplorerNode {
    const target = destination.type;
    let label: string;
    if (destination.displayName && destination.extension) {
      label = `${destination.displayName} (${this._telephoneNumberFilter.transform(destination.extension)})`;
    } else {
      label = destination.displayName || this._telephoneNumberFilter.transform(destination.extension) || '';
    }
    const baseNode: ZiroExplorerNode = {
      title: nodeTitle,
      label: label,
      group: 'greetings',
      previouslyViewedTooltip: 'tkey;migrations.cisco.auto_attendants_report.explorer.previously_viewed.tooltip',
      beforeConnection: 'downward',
    };

    switch (target) {
      case ButtonDestinationType.CALL_HANDLER: {
        const isBackLink =
          this._goBackTouchtoneKeys.includes(buttonNumber) &&
          report.callHandlers.some((otherHandler) => {
            const otherCallerInputs = Object.values(otherHandler.processedCallerInputs) as CallHandlerButton[];
            return otherCallerInputs.some(
              (otherCallHanderButton) => otherCallHanderButton.buttonDestination?.id === callHandler.id,
            );
          });
        return {
          ...baseNode,
          icon: ZiroIcons.AUTO_ATTENDANT,
          sublabel: this._translateService.instant(
            isBackLink
              ? 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.call_handler.sublabel.back'
              : 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.call_handler.sublabel',
            { callHandler: destination.displayName },
          ),
          nextSubchartLink: destination.id,
        };
      }
      case ButtonDestinationType.INTERVIEW_HANDLER:
        return {
          ...baseNode,
          icon: ZiroIcons.AUTO_ATTENDANT,
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.interview_handler.sublabel',
        };
      case ButtonDestinationType.DIRECTORY_HANDLER:
        return {
          ...baseNode,
          icon: ZiroIcons.AUTO_ATTENDANT,
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.directory_handler.sublabel',
        };
      case ButtonDestinationType.CONVERSATION:
        return {
          ...baseNode,
          icon: ZiroIcons.AUTO_ATTENDANT,
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.conversation.sublabel',
        };
      case ButtonDestinationType.USER:
      case ButtonDestinationType.DIRECTORY_NUMBER:
        return {
          ...baseNode,
          icon: ZiroIcons.USER,
          sublabel: 'tkey;migrations.cisco.auto_attendants_report.explorer.destinations.voicemail.sublabel',
        };
    }
  }
}
